const DEFAULT_MAX_PAGES = 100;

export const extractDevicePageRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.vehicles)) return payload.vehicles;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.vehicles)) return payload.data.vehicles;
  return [];
};

const resolveDevicePageMeta = (payload, fallbackLimit) => {
  const nested = payload?.data && !Array.isArray(payload.data) ? payload.data : null;
  const total = Number(payload?.total ?? nested?.total);
  const limit = Number(payload?.limit ?? nested?.limit) || fallbackLimit;
  const hasMore =
    payload?.hasMore !== undefined
      ? Boolean(payload.hasMore)
      : nested?.hasMore !== undefined
        ? Boolean(nested.hasMore)
        : null;

  return {
    total: Number.isFinite(total) ? total : null,
    limit,
    hasMore,
  };
};

export async function accumulateDevicePages({
  fetchPage,
  pageLimit = 200,
  concurrency = 4,
  allowPartialOnLaterError = true,
  maxPages = DEFAULT_MAX_PAGES,
}) {
  const allRows = [];
  const safeConcurrency = Math.max(1, Number(concurrency) || 1);
  const safeMaxPages = Math.max(1, Number(maxPages) || DEFAULT_MAX_PAGES);

  const firstPayload = await fetchPage(1);
  const firstRows = extractDevicePageRows(firstPayload);
  allRows.push(...firstRows);

  const firstMeta = resolveDevicePageMeta(firstPayload, pageLimit);
  const explicitTotal = firstMeta.total;
  const explicitLimit = firstMeta.limit || pageLimit;
  const totalPagesFromCount =
    Number.isFinite(explicitTotal) && explicitTotal > 0 && explicitLimit > 0
      ? Math.ceil(explicitTotal / explicitLimit)
      : null;

  if (totalPagesFromCount && totalPagesFromCount <= 1) {
    return allRows;
  }

  if (!totalPagesFromCount) {
    let page = 2;
    let previousPayload = firstPayload;
    let previousMeta = firstMeta;
    while (page <= safeMaxPages && previousMeta?.hasMore) {
      previousPayload = await fetchPage(page);
      previousMeta = resolveDevicePageMeta(previousPayload, pageLimit);
      const pageRows = extractDevicePageRows(previousPayload);
      allRows.push(...pageRows);
      if (!previousMeta?.hasMore || pageRows.length === 0) break;
      page += 1;
    }
    return allRows;
  }

  const totalPages = Math.min(totalPagesFromCount, safeMaxPages);
  for (let startPage = 2; startPage <= totalPages; startPage += safeConcurrency) {
    const batchPages = [];
    for (
      let page = startPage;
      page < startPage + safeConcurrency && page <= totalPages;
      page += 1
    ) {
      batchPages.push(page);
    }

    try {
      const batchPayloads = await Promise.all(batchPages.map((page) => fetchPage(page)));
      batchPayloads.forEach((payload) => {
        allRows.push(...extractDevicePageRows(payload));
      });
    } catch (error) {
      if (allowPartialOnLaterError && allRows.length > 0) {
        break;
      }
      throw error;
    }
  }

  return allRows;
}

export async function fetchAllDevicesPaginated({
  axios,
  url,
  config,
  params = {},
  pageLimit = 200,
  concurrency = 4,
  allowPartialOnLaterError = true,
}) {
  return accumulateDevicePages({
    pageLimit,
    concurrency,
    allowPartialOnLaterError,
    fetchPage: async (page) => {
      const response = await axios.get(url, {
        ...(config || {}),
        params: {
          ...params,
          page,
          limit: pageLimit,
        },
      });
      return response?.data;
    },
  });
}
